import React, { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useCreateBusinessHours } from '../../../hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { showError, showSuccess } from '../../../utils';
import { DeleteIcon } from '../icons/delete.icon';
import { editFormSchema } from '../../../constants';
import { Spinner } from '../spinner';
import ResetIcon from '../icons/clear-reset.icon';
import { ScheduleConfirmationModal } from '../schedule-confirmation-modal/confirmation-modal';
import { WheelTimePicker } from '../time-picker/time-picker';

export const EditForm = ({ editableData, closeModal, refetch }) => {
  const handleCreateBusinessHours = useCreateBusinessHours();

  const [isLoading, setIsLoading] = useState(false);
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(editFormSchema),
    defaultValues: {
      shifts: [
        {
          startTime: '',
          endTime: '',
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'shifts', // Name for the field array
  });

  const convertData = (data) => {
    let convertedData = [];

    data?.forEach((item) => {
      // Send time strings directly (e.g., "10:00 AM") - no conversion needed
      const newItem = {
        endTime: item.endTime || '',
        startTime: item.startTime || '',
      };

      // Add the new object to the converted data array
      convertedData.push(newItem);
    });

    return convertedData;
  };
  const [scheduleConfirmationModal, setScheduleConfirmationModal] =
    useState(false);
  const [scheduleConfirmationModalData, setScheduleConfirmationModalData] =
    useState(false);

  // Define confirmation callback function
  const confirmationCallback = async (confirmed, formData) => {
    if (confirmed) {
      setIsLoading(true);
      // Proceed with form submission
      const convertedData = convertData(formData?.shifts);
      const payload = {
        input: convertedData,
        shiftDate: editableData?.shiftDate,
      };

      const response = await handleCreateBusinessHours(payload);
      if (response?.data?.setBusinessHoursHistory) {
        showSuccess('Business hours updated successfully');
        refetch();
        closeModal();
        setIsLoading(false);
      } else {
        setIsLoading(false);
        showError(response);
      }
    } else {
      // Handle cancellation
      setIsLoading(false);
      closeScheduleConfirmationModal();
      closeModal();
    }
  };

  const onSubmit = async (data) => {
    openScheduleConfirmationModal(data); // Pass the form data to the confirmation modal
  };

  // Open confirmation modal function
  const openScheduleConfirmationModal = (formData) => {
    setScheduleConfirmationModalData(formData); // Store the form data in state
    setScheduleConfirmationModal(true);
  };

  // Close confirmation modal function
  const closeScheduleConfirmationModal = () => {
    setScheduleConfirmationModal(false);
  };

  useEffect(() => {
    if (editableData?.shiftDate) {
      // startTime and endTime are now returned as formatted strings from backend
      setValue('shifts', editableData.histories);
    }
  }, [editableData]);

  const handleClearField = (index) => {
    const updatedFields = [...fields];
    if (index === 0 && updatedFields.length > 0) {
      updatedFields[0] = {
        endTime: '',
        startTime: '',
      };
      setValue('shifts', updatedFields);
    }
  };

  return (
    <>
      {isLoading && <Spinner />}
      <form className="mt-4  pr-5" onSubmit={handleSubmit(onSubmit)}>
        {fields?.length ? (
          fields.map((item, index) => (
            <div
              key={item.id}
              className="grid grid-cols-gAuto md:gap-4 gap-1 mt-4"
            >
              <div>
                <label className="md:text-base text-xs"> Start Time </label>
                <Controller
                  key={`startTime-${item.id}`}
                  name={`shifts.[${index}].startTime`}
                  control={control}
                  defaultValue={item?.startTime}
                  render={({ field }) => (
                    <WheelTimePicker
                      value={field.value}
                      onChange={(val) => {
                        const updatedFields = [...fields];
                        updatedFields[index].startTime = val;
                        setValue('shifts', updatedFields);
                      }}
                      placeholder="Start Time"
                    />
                  )}
                />
                {errors?.shifts?.[index]?.startTime && (
                  <p className="text-red-drk text-r0.6875">
                    {errors.shifts[index].startTime.message}
                  </p>
                )}
              </div>
              <div>
                <label className="md:text-base text-xs">End Time</label>
                <Controller
                  key={`endTime-${item.id}`}
                  name={`shifts[${index}].endTime`}
                  control={control}
                  defaultValue={item.endTime}
                  render={({ field }) => (
                    <WheelTimePicker
                      value={field.value}
                      onChange={(val) => {
                        const updatedFields = [...fields];
                        updatedFields[index].endTime = val;
                        setValue('shifts', updatedFields);
                      }}
                      placeholder="End Time"
                    />
                  )}
                />
                {errors?.shifts?.[index]?.endTime && (
                  <p className="text-red-drk text-r0.6875">
                    {errors.shifts[index].endTime.message}
                  </p>
                )}
              </div>
              <div className="flex items-center">
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-gray-middle text-xs mt-7 cursor-pointer"
                  >
                    <DeleteIcon />
                  </button>
                )}
                {index === 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      handleClearField(0);
                    }}
                    className="text-gray-middle text-xs mt-7 cursor-pointer"
                  >
                    {/* Clear */}
                    <ResetIcon />
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <></>
        )}
        {fields?.length <= 4 ? (
          <button
            type="button"
            onClick={() => append({ startTime: '', endTime: '' })}
            className="py-2 px-4 mt-4 rounded border border-primary text-primary hover:bg-gray-hover font-semibold"
          >
            Add a Shift
          </button>
        ) : (
          <></>
        )}
        <button
          type="submit"
          className="w-full p-2 mt-4 rounded bg-primary text-white-main hover:bg-blue-hover"
        >
          Save
        </button>
      </form>
      <ScheduleConfirmationModal
        apptCount=""
        scheduleConfirmationModal={scheduleConfirmationModal}
        closeScheduleConfirmationModal={closeScheduleConfirmationModal}
        confirmationCallback={
          (confirmed) =>
            confirmationCallback(confirmed, scheduleConfirmationModalData) // Pass the stored form data to the confirmation callback
        }
        actionType=""
      />
    </>
  );
};
